import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Share2 } from 'lucide-react';

const DISMISS_KEY = 'chaser_install_dismissed_at';
const DISMISS_HOURS = 24;

/**
 * InstallAppBanner - Invites every visitor (non-installed) to install the PWA.
 * - Chrome/Edge/Android: uses beforeinstallprompt when available, otherwise shows browser-specific tip.
 * - iOS Safari: shows Add to Home Screen instructions.
 * - Desktop: also invites and shows manual install steps if native prompt not available.
 */
export default function InstallAppBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [platform, setPlatform] = useState('generic'); // 'ios' | 'android-chrome' | 'desktop-chrome' | 'generic'

  const isStandalone = () => {
    return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true
      || document.referrer.startsWith('android-app://');
  };

  const detectPlatform = () => {
    const ua = window.navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua)
      || (ua.includes('Mac') && 'ontouchend' in document);
    if (isIOS) return 'ios';
    const isAndroid = /Android/.test(ua);
    const isChrome = /Chrome|CriOS|Edg/.test(ua) && !/OPR|Opera/.test(ua);
    if (isAndroid && isChrome) return 'android-chrome';
    if (isChrome) return 'desktop-chrome';
    return 'generic';
  };

  const wasRecentlyDismissed = () => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return false;
      const ts = parseInt(raw, 10);
      if (!ts) return false;
      const hoursPassed = (Date.now() - ts) / (1000 * 60 * 60);
      return hoursPassed < DISMISS_HOURS;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    setPlatform(detectPlatform());

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Always show the invitation after a short delay so users see it on any browser
    const timer = setTimeout(() => setVisible(true), 1500);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        setVisible(false);
        if (outcome === 'dismissed') {
          try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
        }
      } catch {
        setShowHelp(true);
      }
    } else {
      // No native prompt available - show manual instructions
      setShowHelp(true);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    setShowHelp(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  };

  if (!visible) return null;

  const helpContent = {
    ios: {
      title: 'איך להתקין באייפון',
      steps: [
        <>לחץ על כפתור <Share2 className="w-4 h-4 inline text-teal-600" /> <b>שיתוף</b> בתחתית ה-Safari</>,
        <>גלול ובחר <b>"הוסף למסך הבית"</b> (Add to Home Screen)</>,
        <>לחץ על <b>"הוסף"</b> בפינה העליונה - סיימת!</>,
      ],
    },
    'android-chrome': {
      title: 'איך להתקין באנדרואיד',
      steps: [
        <>פתח את התפריט (⋮) בפינה הימנית העליונה של Chrome</>,
        <>בחר <b>"התקן אפליקציה"</b> או <b>"הוסף למסך הבית"</b></>,
        <>אשר את ההתקנה - האפליקציה תופיע במסך הראשי</>,
      ],
    },
    'desktop-chrome': {
      title: 'איך להתקין במחשב',
      steps: [
        <>לחץ על סמל ההתקנה <Download className="w-4 h-4 inline text-teal-600" /> בשורת הכתובת מימין</>,
        <>או פתח את תפריט Chrome (⋮) ובחר <b>"התקן את הצ'ייסר"</b></>,
        <>לחץ <b>"התקן"</b> - האפליקציה תיפתח בחלון עצמאי</>,
      ],
    },
    generic: {
      title: 'איך להתקין את האפליקציה',
      steps: [
        <>פתח את תפריט הדפדפן שלך</>,
        <>בחר <b>"הוסף למסך הבית"</b> או <b>"התקן אפליקציה"</b></>,
        <>אשר את ההוספה למסך הראשי</>,
      ],
    },
  };

  const active = helpContent[platform] || helpContent.generic;

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-md"
        data-testid="install-app-banner"
      >
        <div className="bg-gradient-to-l from-teal-500 to-emerald-600 rounded-2xl shadow-2xl border border-teal-300/40 p-4 flex items-center gap-3">
          <div className="bg-white/20 rounded-xl p-2 flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-black text-sm">התקן את האפליקציה</div>
            <div className="text-teal-50 text-xs mt-0.5">גישה מהירה לצ'ייסר ישירות מהמסך הראשי</div>
          </div>
          <button
            onClick={handleInstall}
            className="bg-white text-teal-700 font-bold text-xs px-3 py-2 rounded-xl hover:bg-teal-50 active:scale-95 transition-all flex items-center gap-1 flex-shrink-0"
            data-testid="install-app-btn"
          >
            <Download className="w-4 h-4" />
            התקן
          </button>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white p-1 flex-shrink-0"
            aria-label="סגור"
            data-testid="install-app-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showHelp && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={handleDismiss}
          data-testid="install-help-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-black text-slate-900">{active.title}</h3>
              <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-700" aria-label="סגור">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-slate-700">
              {active.steps.map((step, idx) => (
                <li key={idx} className="flex gap-3 items-start">
                  <span className="bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <button
              onClick={handleDismiss}
              className="mt-6 w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 rounded-xl transition-colors"
              data-testid="install-help-close"
            >
              הבנתי
            </button>
          </div>
        </div>
      )}
    </>
  );
}
